import { logError, logEvent } from '@cortex/faro';
import { config } from '../../config.js';
import { maskEmailInPath } from '../mask.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business APIエラークラス。HTTPステータス・エラーコード付きのカスタムError
 * @graph-connects none
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    /**
     * エラーレスポンス本体の `error.details`。呼び出し側が復旧に使える構造化情報
     * （例: PJ Sandbox の 409 が返す `currentRevision` / `updatedByName`）を
     * 捨てずに渡すために保持する。値の型はエンドポイントごとに異なるため
     * `unknown` のまま持ち、読む側で絞り込む。
     */
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorResponse {
  error?: {
    message?: string;
    code?: string;
    details?: Record<string, unknown>;
  };
}

/**
 * 401 応答時の OAuth リダイレクト処理。
 * Edge Auth Worker の `X-Edge-Auth-Start` ヘッダがあればそちらへ、
 * なければ旧来の `/login` ルートへ遷移する。open redirect 防止のため
 * Edge Auth ドメインのみ許可。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 401 時の OAuth 開始 URL 解決とフルページ遷移
 * @graph-connects none
 */
function redirectToAuth(edgeAuthStart: string | null): void {
  if (!edgeAuthStart) {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    return;
  }
  const startUrl = new URL(edgeAuthStart);
  // open redirect 防止: origin が Edge Auth ドメインであることを検証
  if (startUrl.origin !== 'https://auth.air-closet.ai') {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    return;
  }
  startUrl.searchParams.set('return', window.location.href);
  window.location.href = startUrl.toString();
}

/**
 * 汎用フェッチラッパー
 * @graph-connects /api/* [calls] Product API の各エンドポイントへの HTTP リクエスト
 */
export async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  // FormData（multipart アップロード）の場合は Content-Type を設定しない。ブラウザが boundary 付きの
  // `multipart/form-data; boundary=...` を自動で付けるため、JSON の Content-Type を固定すると boundary が
  // 欠けてサーバーがパースできなくなる。JSON 経路は従来どおり application/json を付ける。
  const isFormData = options.body instanceof FormData;
  const headers: HeadersInit = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...Object.fromEntries(
      options.headers instanceof Headers
        ? options.headers.entries()
        : Object.entries(options.headers ?? {}),
    ),
  };

  const method = options.method ?? 'GET';
  // ログ送出時は email を含むパス（/accounts/:email など）をマスクする。
  const loggedEndpoint = maskEmailInPath(endpoint);

  const res = await fetch(`${config.apiUrl}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include', // Cookie認証
  });

  if (res.status === 401) {
    const edgeAuthStart = res.headers.get('X-Edge-Auth-Start');
    logEvent('api_unauthorized', {
      endpoint: loggedEndpoint,
      method,
      hasEdgeAuthStart: String(edgeAuthStart !== null),
    });
    redirectToAuth(edgeAuthStart);
    throw new ApiError('Unauthorized', 401, 'UNAUTHORIZED');
  }

  if (!res.ok) {
    const err = (await res.json().catch((error: unknown) => {
      // エラーレスポンス本体が JSON でない場合は HTTP ステータスのみで報告する
      logError(error instanceof Error ? error : new Error(String(error)), {
        event: 'error_response_body_parse_failed',
        endpoint: loggedEndpoint,
        method,
        status: String(res.status),
      });
      return {} as ErrorResponse;
    })) as ErrorResponse;
    const apiError = new ApiError(
      err.error?.message ?? `HTTP ${String(res.status)}`,
      res.status,
      err.error?.code,
      err.error?.details,
    );
    // 5xx だけ Faro に level=error として報告する。4xx (401 は上で処理済み) は
    // backend が認可・バリデーション等で正しく弾いた予期されるレスポンスで、UI 側で
    // ユーザーに surface するため、service error log として扱うと "Service Error Log
    // Detected" alert を 403 等で偽発火させる。trace 用に logEvent で残す。
    // cortex-admin の同名関数と同じ方針 (apps/web/cortex-admin/src/lib/api.ts:82-92)。
    if (res.status >= 500) {
      logError(apiError, {
        event: 'api_request_failed',
        endpoint: loggedEndpoint,
        method,
        status: String(res.status),
        code: apiError.code ?? 'none',
      });
    } else {
      logEvent('api_request_client_error', {
        endpoint: loggedEndpoint,
        method,
        status: String(res.status),
        code: apiError.code ?? 'none',
      });
    }
    throw apiError;
  }

  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}
